<template>
  <teleport to="body">
    <div :class="fadeTransition" class="modal">
      <div class="modal-body" :class="bounceTransition">
        <div class="modal-header">
          <h1>Select Your Label Size</h1>
          <div class="close-svg" v-on:click="closeModal">
            <CloseSVG :propId="'sizes-modal-close'" />
          </div>
        </div>
        <div v-if="idxRef === 0" id="modal-sizes-container">
          <div
            class="modal-sizes"
            v-for="(size, index) in labelSizes"
            :key="index"
            :id="size.toString()"
          >
            <input
              v-if="size === currentLabel.width"
              checked
              name="labelSizeInput"
              type="radio"
              :value="size"
              :id="(index + 100).toString()"
              @input="handleInput($event.target, size)"
            />
            <input
              v-else
              name="labelSizeInput"
              type="radio"
              :value="size"
              :id="(index + 100).toString()"
              @input="handleInput($event.target, size)"
            />
            <label :for="(index + 100).toString()" class="label-option">
              <div id="size-info">
                <p>{{ size }}</p>
              </div>
            </label>
          </div>
        </div>
        <div v-else id="modal-sizes-container">
          <div
            class="modal-sizes"
            v-for="(size, index) in labelSizes"
            :key="index"
            :id="size.toString()"
          >
            <input
              v-if="size === currentLabel.height"
              checked
              name="labelSizeInput"
              type="radio"
              :value="size"
              :id="(index + 100).toString()"
              @input="handleInput($event.target, size)"
            />
            <input
              v-else
              name="labelSizeInput"
              type="radio"
              :value="size"
              :id="(index + 100).toString()"
              @input="handleInput($event.target, size)"
            />
            <label :for="(index + 100).toString()" class="label-option">
              <div id="size-info">
                <p>{{ size }}</p>
              </div>
            </label>
          </div>
        </div>
        <button
          aria-label="Change label size"
          class="modal-select-btn"
          @click="changeLabelSize()"
        >Change Label Size</button>
      </div>
    </div>
  </teleport>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  Ref,
  computed,
  inject,
  onMounted,
  toRef,
  nextTick,
  onUnmounted,
} from "vue";
import { store } from "@/store";
import CloseSVG from "@/components/CloseSVG.vue";
import { ActionTypes } from "@/store/actions";
import { CurrentLabel } from "@/types";

export default defineComponent({
  components: {
    CloseSVG,
  },
  props: {
    idx: Number,
  },
  setup(props) {
    const idxRef = toRef(props, "idx");
    if (idxRef.value !== 0 && idxRef.value !== 1) {
      throw new Error("Missing necessary prop, CustomLabelSizes.vue");
    }

    const closeModal = inject("closeModal") as () => void;
    const bounceTransition = inject("bounceTransition") as Ref<string>;
    const fadeTransition = inject("fadeTransition") as Ref<string>;

    const currentLabel = computed<CurrentLabel>(() => store.getters.currentLabel);
    const customLabelSizes = computed<[number[], number[]]>(() => store.getters.customLabelSizes);

    const labelSizes = ref<number[]>([]);
    onMounted(async () => {
      labelSizes.value = customLabelSizes.value[idxRef.value ? idxRef.value : 0];

      if (idxRef.value === 0) {
        const foundSize = labelSizes.value.find(
          (label: number) => label === currentLabel.value.width
        );

        await nextTick();
        if (foundSize) {
          const sizeContainer = document.getElementById(foundSize.toString());

          if (sizeContainer) {
            sizeContainer.style.background = "#0096ff15";

            const listContainer = document.getElementById("modal-sizes-container");

            if (listContainer) {
              const sizeContainerBoundingRect = sizeContainer.getBoundingClientRect();
              const listContainerBoundingRect = listContainer.getBoundingClientRect();

              // prettier-ignore
              listContainer.scroll(0, sizeContainerBoundingRect.top - listContainerBoundingRect.top - sizeContainerBoundingRect.height / 2);
            }
          }
        }
      } else {
        const foundSize = labelSizes.value.find(
          (label: number) => label === currentLabel.value.height
        );

        await nextTick();
        if (foundSize) {
          const sizeContainer = document.getElementById(foundSize.toString());

          if (sizeContainer) {
            sizeContainer.style.background = "#0096ff15";

            const listContainer = document.getElementById("modal-sizes-container");

            if (listContainer) {
              const sizeContainerBoundingRect = sizeContainer.getBoundingClientRect();
              const listContainerBoundingRect = listContainer.getBoundingClientRect();

              // prettier-ignore
              listContainer.scroll(0, sizeContainerBoundingRect.top - listContainerBoundingRect.top - sizeContainerBoundingRect.height / 2);
            }
          }
        }
      }
    });

    const currentlySelectedSize = ref(
      idxRef.value === 0 ? currentLabel.value.width : currentLabel.value.height
    );
    const selectedLabelSize = ref(0);

    const validateTarget = (target: EventTarget | null): target is HTMLInputElement => {
      return target !== null && target !== undefined;
    };

    const handleInput = (target: EventTarget | null, id: number): void => {
      if (validateTarget(target)) {
        const selectedSize = target.value;
        const foundSize = labelSizes.value.find((label: number) => label === Number(selectedSize));

        if (foundSize) {
          const sizeContainer = document.getElementById(id.toString());
          const prevSizeContainer = document.getElementById(currentlySelectedSize.value.toString());

          if (sizeContainer && prevSizeContainer) {
            prevSizeContainer.style.background = "";

            sizeContainer.style.background = "#0096ff15";
            currentlySelectedSize.value = id;
          }

          selectedLabelSize.value = foundSize;
        }
      }
    };

    const changeLabelSize = async (): Promise<void> => {
      await store.dispatch(ActionTypes.CHANGE_CUSTOM_LABEL_SIZE, {
        dimension: idxRef.value === 0 ? "width" : "height",
        value: selectedLabelSize.value,
      });
      closeModal();
    };

    return {
      idxRef,
      closeModal,
      bounceTransition,
      fadeTransition,
      currentLabel,
      labelSizes,
      handleInput,
      changeLabelSize,
    };
  },
});
</script>

<style scoped>
.modal-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  background-color: white;
  width: 500px;
  height: 600px;
  padding: 25px;
  border-radius: 6px;
  overflow: auto;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 5%;
}

.modal-header h1 {
  font-weight: 600;
  font-size: 22px;
  letter-spacing: -0.25px;
}

.close {
  border-radius: 50%;
  border: solid 1px black;
  background-color: white;
  height: 25px;
  width: 25px;
}

#modal-sizes-container {
  overflow-y: scroll;
  height: 450px;
  width: 100%;
  border: 1px solid rgb(164, 160, 160);
}

.modal-sizes {
  height: 100px;
  border: 1px solid rgb(229, 229, 229);
  padding: 10px;
  margin: 10px;
  display: flex;
  align-items: center;
}

.label-option {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 700;
  padding: 10px;
}

.label-option img {
  margin-right: 10px;
  width: 150px;
  height: 150px;
}

.modal-select-btn {
  border: none;
  border-radius: 2px;
  background: #ff6c00;
  color: white;
  font-size: 16px;
  height: 40px;
  padding: 0 2px 0 2px;
  text-decoration: none;
  text-align: center;
  width: 100%;
  text-shadow: none;
}
</style>
